import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Link as LinkIcon,
  Upload as UploadIcon,
  Crop as CropIcon
} from '@mui/icons-material';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { apiUrl } from '../utils/api';

interface LogoUploadProps {
  companyId: number | null;
  currentLogoUrl: string;
  onLogoChange: (logoUrl: string) => void;
  onUrlChange: (url: string) => void;
  onPendingImageChange?: (blob: Blob | null) => void;
  disabled?: boolean;
}

async function getCroppedImg(
  image: HTMLImageElement,
  crop: PixelCrop
): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width * scaleX,
    crop.height * scaleY
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/png');
  });
}

const LogoUpload: React.FC<LogoUploadProps> = ({
  companyId,
  currentLogoUrl,
  onLogoChange,
  onUrlChange,
  onPendingImageChange,
  disabled = false
}) => {
  const [uploadMode, setUploadMode] = useState<'url' | 'file'>('url');
  const [logoUrl, setLogoUrl] = useState(currentLogoUrl);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: 25,
    y: 25,
    width: 50,
    height: 50
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string>('');
  const [urlImageValid, setUrlImageValid] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imageLoadError, setImageLoadError] = useState(false);

  const validateUrl = (url: string) => {
    if (!url) return '';
    try {
      new URL(url);
      return '';
    } catch {
      return 'Invalid URL format (must include http:// or https://)';
    }
  };

  // Check if URL image is valid and loadable
  const checkUrlImage = (url: string) => {
    if (!url || validateUrl(url)) {
      setUrlImageValid(false);
      return;
    }

    const img = new Image();
    img.onload = () => setUrlImageValid(true);
    img.onerror = () => setUrlImageValid(false);
    img.src = url;
  };

  const handleModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'url' | 'file' | null) => {
    if (newMode !== null) {
      setUploadMode(newMode);
      setError(null);
      setUrlError('');
    }
  };

  const handleUrlChange = (url: string) => {
    setLogoUrl(url);
    onUrlChange(url);
    const validationError = validateUrl(url);
    setUrlError(validationError);
    checkUrlImage(url);
  };

  // Convert URL to data URL to avoid CORS issues
  const urlToDataUrl = async (url: string): Promise<string> => {
    try {
      // Include auth token for proxy endpoints
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, { headers });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      throw new Error('Failed to load image');
    }
  };

  // Handle crop from URL
  const handleCropFromUrl = async () => {
    if (logoUrl && !validateUrl(logoUrl)) {
      setError(null);
      setImageLoadError(false);
      try {
        // Convert to data URL to avoid CORS issues
        const dataUrl = await urlToDataUrl(logoUrl);
        setSelectedImage(dataUrl);
        setCropDialogOpen(true);
      } catch (err) {
        setError('Failed to load image for cropping. Please try uploading a new image.');
      }
    }
  };

  // Handle crop from current logo (Azure URL)
  const handleCropCurrentLogo = async () => {
    if (currentLogoUrl && !currentLogoUrl.startsWith('blob:')) {
      setError(null);
      setImageLoadError(false);
      try {
        // Use proxy endpoint to bypass CORS for Azure URLs
        if (companyId && currentLogoUrl.includes('blob.core.windows.net')) {
          const proxyUrl = apiUrl(`/api/companies/${companyId}/logo-proxy`);
          const dataUrl = await urlToDataUrl(proxyUrl);
          setSelectedImage(dataUrl);
          setCropDialogOpen(true);
        } else {
          // For non-Azure URLs, try direct fetch
          const dataUrl = await urlToDataUrl(currentLogoUrl);
          setSelectedImage(dataUrl);
          setCropDialogOpen(true);
        }
      } catch (err) {
        setError('Failed to load current logo for cropping. Please try uploading a new image.');
      }
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        setError('Invalid file type. Please select PNG, JPG, or GIF image.');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File too large. Maximum size is 5MB.');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
        setCropDialogOpen(true);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    imgRef.current = e.currentTarget;

    // Set initial crop to center square
    const { width, height } = e.currentTarget;
    const cropSize = Math.min(width, height) * 0.5;
    const x = (width - cropSize) / 2;
    const y = (height - cropSize) / 2;

    setCrop({
      unit: 'px',
      x,
      y,
      width: cropSize,
      height: cropSize
    });
  };

  const handleCropSave = async () => {
    if (!imgRef.current || !completedCrop) {
      setError('Please select a crop area');
      return;
    }

    try {
      setUploading(true);
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);

      if (!croppedBlob) {
        throw new Error('Failed to crop image');
      }

      // Always save blob for later upload (on Save button click)
      if (onPendingImageChange) {
        onPendingImageChange(croppedBlob);
      }
      setCropDialogOpen(false);
      setSelectedImage(null);
      setError(null);

      // Create a temporary preview URL
      const previewUrl = URL.createObjectURL(croppedBlob);
      setLogoUrl(previewUrl);
      onUrlChange(previewUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error processing logo');
    } finally {
      setUploading(false);
    }
  };

  const handleCropCancel = () => {
    setCropDialogOpen(false);
    setSelectedImage(null);
    setCrop({
      unit: '%',
      x: 25,
      y: 25,
      width: 50,
      height: 50
    });
    setCompletedCrop(null);
  };

  return (
    <Box>
      {/* Toggle between URL and File Upload */}
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={uploadMode}
          exclusive
          onChange={handleModeChange}
          size="small"
          fullWidth
          disabled={disabled}
        >
          <ToggleButton value="url" sx={{ flex: 1 }}>
            <LinkIcon sx={{ mr: 1 }} />
            URL
          </ToggleButton>
          <ToggleButton value="file" sx={{ flex: 1 }}>
            <UploadIcon sx={{ mr: 1 }} />
            Upload File
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* URL Input */}
      {uploadMode === 'url' && (
        <Box>
          <TextField
            label="Logo URL"
            value={logoUrl}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="https://example.com/logo.png"
            size="small"
            fullWidth
            disabled={disabled}
            error={!!urlError}
            helperText={urlError || 'Enter a valid URL to your logo'}
          />
          {/* Crop button for URL images */}
          {urlImageValid && logoUrl && !urlError && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CropIcon />}
              onClick={handleCropFromUrl}
              disabled={disabled || uploading}
              sx={{ mt: 1 }}
              fullWidth
            >
              Crop This Image
            </Button>
          )}
        </Box>
      )}

      {/* File Upload */}
      {uploadMode === 'file' && (
        <Box>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            disabled={disabled || uploading}
            startIcon={<UploadIcon />}
          >
            {uploading ? 'Processing...' : 'Choose Logo & Crop'}
            <input
              type="file"
              hidden
              accept="image/png,image/jpeg,image/jpg,image/gif"
              onChange={handleFileSelect}
            />
          </Button>
          {!companyId && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Logo will be uploaded after you save the company
            </Alert>
          )}
        </Box>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Logo Preview */}
      {currentLogoUrl && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Current Logo:
          </Typography>
          <Box
            component="img"
            src={currentLogoUrl}
            alt="Company Logo"
            sx={{
              maxWidth: '100%',
              maxHeight: 100,
              mt: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1
            }}
          />
          {/* Crop existing logo button - temporarily disabled due to CORS issues with Azure
          {currentLogoUrl && !currentLogoUrl.startsWith('blob:') &&
           !(uploadMode === 'url' && urlImageValid && logoUrl && !urlError) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CropIcon />}
              onClick={handleCropCurrentLogo}
              disabled={disabled || uploading}
              sx={{ mt: 1 }}
            >
              Crop Current Logo
            </Button>
          )}
          */}
        </Box>
      )}

      {/* Crop Dialog */}
      <Dialog
        open={cropDialogOpen}
        onClose={handleCropCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <CropIcon />
              <Typography variant="h6">Crop Logo</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Drag to select crop area
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}>
            {/* Crop Area */}
            <Box sx={{
              maxWidth: '100%',
              maxHeight: 500,
              overflow: 'auto',
              bgcolor: '#f5f5f5',
              borderRadius: 2,
              p: 2
            }}>
              {selectedImage && (
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  circularCrop={false}
                  ruleOfThirds={true}
                  minWidth={50}
                  minHeight={50}
                >
                  <img
                    src={selectedImage}
                    alt="Crop preview"
                    onLoad={onImageLoad}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '450px',
                      display: 'block'
                    }}
                    crossOrigin="anonymous"
                  />
                </ReactCrop>
              )}
            </Box>

            {/* Instructions */}
            <Alert severity="info" sx={{ width: '100%' }}>
              <Typography variant="caption">
                Click and drag to select the area you want to crop.
                Drag corners or edges to resize. Drag inside to move.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCropCancel} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleCropSave}
            variant="contained"
            disabled={uploading || !completedCrop}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
          >
            {uploading ? 'Processing...' : 'Save & Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LogoUpload;
